<template>
  <div class="catalog-rules">
    <CatalogLockNotice
      title="Twee lagen regels"
      text="De regels van de leverancier komen uit de prijslijst. Je kunt ze aan- of uitzetten, niet bewerken. Daaronder maak je je eigen regels. Die blijven staan als er een nieuwe prijslijst komt."
    />

    <p v-if="error" class="catalog-rules__error">{{ error }}</p>

    <Card title="Regels van de leverancier" :subtitle="`Versie ${versionNumber || '–'}`">
      <p v-if="!masterRules.length" class="catalog-rules__empty">
        Deze prijslijst heeft geen regels die je kunt uitzetten.
      </p>
      <div v-for="group in groupedMasterRules" :key="group.type" class="catalog-rules__group">
        <h4 class="catalog-rules__group-title">{{ group.label }}</h4>
        <label v-for="rule in group.rules" :key="rule.id" class="catalog-rules__rule">
          <ToggleInput
            :id="`master-rule-${rule.id}`"
            :model-value="rule.enabled"
            @update:model-value="setMasterRule(rule.id, $event)"
          />
          <span :class="{ 'catalog-rules__rule--off': !rule.enabled }">{{ rule.label }}</span>
        </label>
      </div>
    </Card>

    <Card title="Jouw aantalregels" class="mt-6" subtitle="Staffels op aantal of een opgeteld veld">
      <VolumePricingRulesEditor
        v-model="dealerRules.volume_pricing_rules"
        :allowed-scopes="['same_product']"
        :product-name="productName"
        :basis-field-options="basisFieldOptions"
      />
    </Card>

    <Card
      v-if="productOptions.length"
      title="Jouw cross-option regels"
      class="mt-6"
      subtitle="Prijsaanpassingen op combinaties van opties"
    >
      <CrossOptionRulesEditor
        v-model="dealerRules.cross_option_rules"
        :product-options="productOptions"
        :computed-fields="computedFields"
      />
    </Card>

    <Card
      title="Jouw auto-include items"
      class="mt-6"
      subtitle="Items die jij altijd meelevert, zoals montage"
    >
      <AutoIncludeRulesEditor
        v-model="dealerRules.auto_include_rules"
        :product-options="productOptions"
        :computed-fields="computedFields"
      />
    </Card>

    <div class="catalog-rules__footer">
      <Btn variant="primary" :loading="saving" :disabled="!loaded" @click="save">
        Regels opslaan
      </Btn>
    </div>
  </div>
</template>

<script>
  import { computed, onMounted, reactive, ref } from 'vue';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import CatalogLockNotice from '@/components/products/CatalogLockNotice.vue';
  import VolumePricingRulesEditor from '@/components/products/VolumePricingRulesEditor.vue';
  import CrossOptionRulesEditor from '@/components/products/CrossOptionRulesEditor.vue';
  import AutoIncludeRulesEditor from '@/components/products/AutoIncludeRulesEditor.vue';
  import { catalogService } from '@/services/catalogService';
  import { useToastStore } from '@/store/toastStore';

  const TYPE_LABELS = {
    volume_pricing_rules: 'Aantalregels',
    cross_option_rules: 'Cross-option regels',
    auto_include_rules: 'Auto-include items',
    upsells: 'Upsells',
  };

  const emptyRules = () => ({
    volume_pricing_rules: [],
    cross_option_rules: [],
    auto_include_rules: [],
    upsells: [],
  });

  export default {
    name: 'CatalogRulesTab',
    components: {
      Card,
      Btn,
      ToggleInput,
      CatalogLockNotice,
      VolumePricingRulesEditor,
      CrossOptionRulesEditor,
      AutoIncludeRulesEditor,
    },
    props: {
      installationId: { type: String, required: true },
      productOptions: { type: Array, default: () => [] },
      computedFields: { type: Array, default: () => [] },
      productName: { type: String, default: 'dit product' },
      basisFieldOptions: { type: Array, default: () => [] },
    },
    emits: ['saved'],
    setup(props, { emit }) {
      const toastStore = useToastStore();
      const loaded = ref(false);
      const saving = ref(false);
      const error = ref('');
      const versionNumber = ref(null);
      const masterRules = ref([]);
      const dealerRules = reactive(emptyRules());

      const groupedMasterRules = computed(() =>
        Object.keys(TYPE_LABELS)
          .map((type) => ({
            type,
            label: TYPE_LABELS[type],
            rules: masterRules.value.filter((rule) => rule.type === type),
          }))
          .filter((group) => group.rules.length > 0)
      );

      const load = async () => {
        error.value = '';
        try {
          const result = await catalogService.getInstallation(props.installationId);
          versionNumber.value = result.version?.version_number || null;
          masterRules.value = result.dealer?.rules || [];
          Object.assign(dealerRules, emptyRules(), result.dealer?.dealer_rules || {});
          loaded.value = true;
        } catch (err) {
          error.value = err?.response?.data?.error || 'Regels laden mislukt';
        }
      };

      const setMasterRule = (id, enabled) => {
        const rule = masterRules.value.find((item) => item.id === id);
        if (rule) rule.enabled = enabled;
      };

      const save = async () => {
        saving.value = true;
        error.value = '';
        try {
          const result = await catalogService.updateInstallation(props.installationId, {
            dealer_rules: {
              volume_pricing_rules: dealerRules.volume_pricing_rules,
              cross_option_rules: dealerRules.cross_option_rules,
              auto_include_rules: dealerRules.auto_include_rules,
              upsells: dealerRules.upsells,
            },
            disabled_rule_ids: masterRules.value
              .filter((rule) => !rule.enabled)
              .map((rule) => rule.id),
          });
          toastStore.addToast({ type: 'success', message: 'Regels opgeslagen' });
          emit('saved', result);
        } catch (err) {
          const details = err?.response?.data?.details;
          error.value =
            (Array.isArray(details) && details[0]?.message) ||
            err?.response?.data?.error ||
            'Opslaan mislukt';
        } finally {
          saving.value = false;
        }
      };

      onMounted(load);

      return {
        loaded,
        saving,
        error,
        versionNumber,
        masterRules,
        groupedMasterRules,
        dealerRules,
        setMasterRule,
        save,
      };
    },
  };
</script>

<style scoped lang="scss">
  .catalog-rules {
    &__group {
      padding: 0.5rem 0;

      & + & {
        border-top: 1px solid var(--slate-100);
      }
    }

    &__group-title {
      margin: 0 0 0.35rem;
      color: var(--slate-600);
      @include fontSize('sm');
    }

    &__rule {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.35rem 0;

      &--off {
        color: var(--slate-400);
        text-decoration: line-through;
      }
    }

    &__empty {
      margin: 0;
      color: var(--slate-500);
    }

    &__error {
      margin: 0 0 1rem;
      color: var(--red-700);
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      margin-top: 1.5rem;
    }
  }
</style>
